import { usePresence } from "motion/react";
import React, { useRef } from "react";
import { Provider, useStore } from "react-redux";

/**
 * Freezes the subtree when exiting an AnimatePresence boundary by providing
 * a static Redux store snapshot. All useSelector hooks inside the subtree
 * will keep returning the last values from before the exit started.
 * Must be used inside an AnimatePresence boundary.
 */
export const FreezeOnExit = ({ children }: { children: React.ReactNode }) => {
  const [isPresent] = usePresence();
  const store = useStore();
  const frozenState = useRef(store.getState());

  if (isPresent) {
    frozenState.current = store.getState();
  }

  if (!isPresent) {
    const staticStore = {
      getState: () => frozenState.current,
      subscribe: () => () => {},
      dispatch: store.dispatch,
      replaceReducer: store.replaceReducer,
      [Symbol.observable]: store[Symbol.observable],
    } as typeof store;

    return <Provider store={staticStore}>{children}</Provider>;
  }

  return <>{children}</>;
};
